import * as React from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import {
    Box,
    TextInput,
    Group,
    Button,
    Textarea,
    Switch,
} from '@mantine/core'
import { useForm, isNotEmpty } from "@mantine/form";
import axios from 'axios';
import type { IResponse } from "../../types/api";
import { showMessage } from "../../lib/notification";
import { CLIENT_ROUTE, API_ROUTE, CLIENT_TITLE } from "./constants";
import produce from "immer";
import { DatePicker } from "@mantine/dates";
import type { IArticle } from "./types";
import dayjs from 'dayjs';
import { FileWithPath, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import FileUpload from "../../components/file-upload";
import Page from "../../components/layout/Page";
import { assignFiles } from "../../utils/form";
import { useFormStyles } from "../../styles/form";

interface FormValues extends Pick<IArticle, 'content' | 'title' | 'isPublished' | 'shortContent' | 'isDeleted' | 'createdUserName'> {
    publishedAt: Date | null | string;
    image: string | FileWithPath | null;
    mobileImage: string | FileWithPath | null;
}

interface IParams extends Omit<FormValues, 'image' | 'mobileImage'> {
    image?: string;
    mobileImage?: string;
}

const EditArticle = () => {

    const { state: id = null } = useLocation();

    const { classes } = useFormStyles()
    const navigate = useNavigate();

    const form = useForm<FormValues>({
        initialValues: {
            content: '',
            title: '',
            isPublished: false,
            shortContent: '',
            createdUserName: '',
            image: null,
            mobileImage: null,
            isDeleted: false,
            publishedAt: null,
        },

        validate: {
            title: isNotEmpty('Гарчиг оруулна уу'),
            content: isNotEmpty('Мэдээ оруулна уу'),
        },
    });

    React.useEffect(() => {
        const fetchData = async () => {
            const { data } = await axios.get<IResponse<IArticle>>(`${API_ROUTE}/${id}`);
            const values = produce(data.result, draft => {
                const publishedAt = dayjs(draft.publishedAt);
                if (publishedAt.isValid()) {
                    draft.publishedAt = publishedAt.toDate();
                }
            })
            form.setValues(values);
            form.resetDirty();
        }
        void fetchData();
    }, []);

    const handleSubmit = async (values: typeof form.values) => {
        const files = await assignFiles({
            image: values.image,
            mobileImage: values.mobileImage
        }, 'article')
        const params: IParams = {
            ...values,
            ...files
        }
        const { data } = await axios.post<IResponse<any>>(`${API_ROUTE}/${id}`, params);
        if (data.statusCode === 200) {
            navigate(CLIENT_ROUTE);
            showMessage('Амжилттай', 'green')
        } else {
            showMessage('Алдаа гарлаа')
        }
    }

    return (
        <Page>
            <Page.Header title={`${CLIENT_TITLE} засах`} backRoute={CLIENT_ROUTE}/>
            <Page.Body>
                <Box sx={{ maxWidth: 500 }} mx="auto">
                    <form onSubmit={form.onSubmit(handleSubmit)} className={classes.form}>
                        <TextInput
                            withAsterisk
                            label="Гарчиг"
                            placeholder="Гарчиг оруулна уу"
                            {...form.getInputProps('title')}
                        />

                        <Textarea
                            autosize
                            label="Товч"
                            placeholder="Товч"
                            {...form.getInputProps('shortContent')}
                        />

                        <Textarea
                            withAsterisk
                            autosize
                            label="Мэдээ"
                            placeholder="Мэдээ оруулна уу"
                            {...form.getInputProps('content')}
                        />

                        <FileUpload
                            value={form.values.image}
                            onChange={(value) => {
                                form.setFieldValue('image', value);
                            }}
                            label='Зураг'
                            accept={IMAGE_MIME_TYPE}
                        />

                        <FileUpload
                            value={form.values.mobileImage}
                            onChange={(value) => {
                                form.setFieldValue('mobileImage', value);
                            }}
                            label='Утасны зураг'
                            accept={IMAGE_MIME_TYPE}
                        />

                        <Switch
                            label="Нийтлэгдсэн эсэх"
                            {...form.getInputProps('isPublished', { type: 'checkbox' })}
                        />

                        <DatePicker
                            disabled
                            placeholder="Нийтлэгдсэн өдөр"
                            label="Нийтлэгдсэн өдөр"
                            {...form.getInputProps('publishedAt')}
                        />

                        <TextInput
                            label="Үүсгэсэн хэрэглэгч нэр"
                            placeholder="Үүсгэсэн хэрэглэгч нэр"
                            disabled
                            {...form.getInputProps('createdUserName')}
                        />
                    </form>
                </Box>
            </Page.Body>
            <Page.Footer>
                <Group position="right" mt="md">
                    <Button color='color-secondary' onClick={() => form.onSubmit(handleSubmit)()}>Хадгалах</Button>
                </Group>
            </Page.Footer>
        </Page>
    );
};

export default EditArticle;